import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Snackbar, Button } from '@mui/material';
import { chatAPI } from '../services/api';
import { registerServiceWorker, syncPushSubscription, setAppBadge } from '../services/push';

/**
 * Lo que el gestor necesita de los chats estando en cualquier pantalla: el
 * contador de no leidos (menu y contador del icono de la app), la suscripcion a
 * las notificaciones y la reaccion a una notificacion pulsada o recibida con la
 * app abierta. La app del tecnico hace lo mismo en TechnicianApp.
 */
const ChatContext = createContext({ unread: 0, refreshUnread: () => {} });

const UNREAD_POLL_MS = 30000;

export function ChatProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [toast, setToast] = useState(null);

  // El chat abierto, para no avisar de mensajes de la conversacion que se esta leyendo
  const openChatRef = useRef(null);
  openChatRef.current = location.pathname === '/chats'
    ? Number(new URLSearchParams(location.search).get('p')) || null
    : null;

  const refreshUnread = useCallback(() => {
    chatAPI.unread()
      .then((res) => { setUnread(res.data.total); setAppBadge(res.data.total); })
      .catch(() => {});
  }, []);

  // El service worker se registra siempre, y la suscripcion se refresca en cada
  // arranque si el permiso ya esta dado: el navegador puede cambiar el endpoint
  useEffect(() => {
    registerServiceWorker().then(() => syncPushSubscription()).catch(() => {});
  }, []);

  useEffect(() => {
    refreshUnread();
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') refreshUnread();
    }, UNREAD_POLL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') refreshUnread(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refreshUnread, location.pathname]);

  useEffect(() => {
    const onMessage = (event) => {
      const { type, data, projectId } = event.data || {};
      if (type === 'open-chat' && projectId) navigate(`/chats?p=${projectId}`);
      if (type === 'chat-push') {
        refreshUnread();
        if (Number(data?.project_id) !== openChatRef.current) setToast(data);
      }
    };
    navigator.serviceWorker?.addEventListener('message', onMessage);
    return () => navigator.serviceWorker?.removeEventListener('message', onMessage);
  }, [navigate, refreshUnread]);

  return (
    <ChatContext.Provider value={{ unread, refreshUnread }}>
      {children}
      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        message={toast ? `${toast.title}: ${toast.body}` : ''}
        action={toast?.project_id ? (
          <Button color="secondary" size="small"
            onClick={() => { navigate(`/chats?p=${toast.project_id}`); setToast(null); }}>
            Ver
          </Button>
        ) : null} />
    </ChatContext.Provider>
  );
}

export const useChat = () => useContext(ChatContext);
